import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { UserState } from '../user/UserState';
import { OrganizationsState } from './OrganizationsState';

const AGENCY_TABS = ['insights', 'clients', 'team'] as const;
export type IAgencyTab = typeof AGENCY_TABS[number];
export const isAgencyTab = (value: string | null): value is IAgencyTab => AGENCY_TABS.some(tab => tab === value);

export const AGENCY_TAB_PARAM = 'tab';

/** How a caller asks the agency home to open on a tab other than the one it leads with. */
export const agencyTabSearch = (tab: IAgencyTab) => `${AGENCY_TAB_PARAM}=${tab}`;

const useStyles = createUseStyles({
    // A caseload tool opens on work, not on imagery, so the header earns its height by carrying the
    // things a case manager needs standing: whose book this is, how big it is, and the way between
    // the two views of it.
    band: {
        position: 'relative',
        overflow: 'hidden',
        padding: '16px 24px 0',
        background: 'linear-gradient(180deg, var(--ion-color-primary) 0%, var(--ion-color-primary-shade) 100%)',

        '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(120% 140% at 82% 0%, rgba(238, 108, 78, 0.22), transparent 62%)',
            pointerEvents: 'none'
        }
    },

    inner: {
        position: 'relative',
        zIndex: 1
    },

    top: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap' as const
    },

    orgName: {
        margin: 0,
        color: '#fff',
        fontSize: 19,
        fontWeight: 600,
        letterSpacing: '-0.01em'
    },

    greeting: {
        fontSize: 13,
        color: 'rgba(255, 255, 255, 0.72)'
    },

    tabs: {
        display: 'flex',
        gap: 2,
        marginTop: 14
    },

    tab: {
        background: 'none',
        border: 0,
        borderBottom: '2.5px solid transparent',
        padding: '9px 16px',
        font: 'inherit',
        fontSize: 14,
        color: 'rgba(255, 255, 255, 0.66)',
        cursor: 'pointer'
    },

    tabOn: {
        color: '#fff',
        fontWeight: 500,
        borderBottomColor: 'var(--ion-color-secondary)'
    }
});

export const AgencyHomeHeader: FC<{
    orgId: string;
    tab: IAgencyTab;
    onChangeTab: (tab: IAgencyTab) => void;
}> = ({ orgId, tab, onChangeTab }) => {
    const classes = useStyles();

    const orgState = useInstance(OrganizationsState);
    useSubscription(() => orgState);

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const org = orgState.getOrganization(orgId);
    if (!org) {
        return null;
    }

    const name = userState.state.firstName || '';

    // Insights leads because an agency opens this page to find out where to look, not to page
    // through a roster they already know. Clients stays one click away for when they have a name.
    const tabs: { label: string; value: IAgencyTab }[] = [
        { label: 'Insights', value: 'insights' },
        { label: 'Clients', value: 'clients' },
        { label: 'Team', value: 'team' }
    ];

    return (
        <header className={classes.band} data-type="agency-home-header">
            <div className={classes.inner}>
                <div className={classes.top}>
                    <h1 className={classes.orgName} data-type="org-name-heading">{org.name}</h1>
                    {name && <div className={`${classes.greeting} mp-mask`}>{name}</div>}
                </div>

                <div className={classes.tabs}>
                    {tabs.map(({ label, value }) => (
                        <button
                            key={value}
                            type="button"
                            className={`${classes.tab} ${tab === value ? classes.tabOn : ''}`}
                            data-id={`agency-tab-${value}`}
                            aria-current={tab === value}
                            onClick={() => onChangeTab(value)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>
        </header>
    );
};
